import { useEffect, useState } from 'react';
import type { AssistantProfile, AssistantStats } from '@reef/schema';
import { api } from '../api.ts';

/**
 * The assistant, on the web.
 *
 * One of three surfaces and the least important of them — most questions are
 * asked in Slack, where the question was going to be asked anyway. This one
 * exists because the studio is where somebody already is when they are looking
 * at a run and want to know what a service is, and because a question asked
 * here continues the same durable thread as one asked in Slack: same person,
 * same profile, same conversation.
 *
 * The profile picker only lists what this person may use. A team profile
 * carries that team's own standards, so offering one to somebody outside the
 * team would be a small, quiet leak of how another team works.
 */
export function AssistantView() {
  const [profiles, setProfiles] = useState<AssistantProfile[]>([]);
  const [stats, setStats] = useState<AssistantStats | null>(null);
  const [profile, setProfile] = useState('default');
  const [question, setQuestion] = useState('');
  const [thread, setThread] = useState<Array<{ question: string; answer: string }>>([]);
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .assistantProfiles()
      .then((result) => {
        setProfiles(result.profiles);
        setStats(result.stats);
      })
      .catch((caught) => setError((caught as Error).message));
  }, []);

  const ask = async () => {
    const asked = question.trim();
    if (!asked || asking) return;

    setAsking(true);
    setError(null);
    setQuestion('');

    try {
      const result = await api.ask(asked, profile);
      setThread((previous) => [...previous, { question: asked, answer: result.answer }]);
    } catch (caught) {
      setError((caught as Error).message);
      // Put the question back rather than losing it: a failed ask is usually a
      // transient one, and retyping is the part people resent.
      setQuestion(asked);
    } finally {
      setAsking(false);
    }
  };

  return (
    <div className="studio">
      <header className="studio-head">
        <span className="studio-crumb">
          <span className="studio-crumb-root">Assistant</span>
        </span>
        <span className="spacer" />
        {stats && <span className="chip">{stats.sessions} questions in the window</span>}
      </header>

      <div className="studio-column wide">
        {error && <div className="banner danger">{error}</div>}

        <section className="studio-card">
          <div className="studio-card-head">
            <h2>Ask</h2>
            <select
              value={profile}
              onChange={(event) => setProfile(event.target.value)}
              aria-label="Assistant profile"
            >
              {profiles.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.name}
                </option>
              ))}
            </select>
          </div>
          <div className="studio-card-body">
            <p className="hint" style={{ marginTop: 0 }}>
              {profiles.find((entry) => entry.id === profile)?.description ??
                'Answers from the context graph, the codebase index, the skills registry and the run history.'}
            </p>

            <form
              onSubmit={(event) => {
                event.preventDefault();
                void ask();
              }}
              style={{ display: 'flex', gap: '0.5rem' }}
            >
              <input
                value={question}
                onChange={(event) => setQuestion(event.target.value)}
                placeholder="who owns checkout? what breaks if I change parseBody?"
                aria-label="Question"
                style={{ flex: 1 }}
                disabled={asking}
              />
              <button type="submit" disabled={asking || question.trim().length === 0}>
                {asking ? 'Looking…' : 'Ask'}
              </button>
            </form>

            <p className="hint">
              Read-only. The assistant can look things up and cannot start a run, edit a workflow or
              touch a connection — so it can sit in every channel without a conversation about blast
              radius.
            </p>
          </div>
        </section>

        {thread.map((entry, index) => (
          <section className="studio-card" key={`${index}-${entry.question}`}>
            <div className="studio-card-head">
              <h2>{entry.question}</h2>
            </div>
            <div className="studio-card-body">
              <pre className="studio-instructions">{entry.answer}</pre>
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
